import { useState } from 'react';
import { notification } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import IconButton from '@mui/material/IconButton';

import { creditHttp } from '@network';
import { Document, DocumentFile, FileStatus } from '@types';
import { axiosErrorToString, rbac, ROLES } from '@utils';
import { LightTooltip } from '@components';
import cls from './document-file-status.module.css';

interface Props {
  creditId: number;
  file: DocumentFile;
  document: Document;
  setDocument: (document: Document) => void;
}

export const DocumentFileStatus = ({ creditId, file, document, setDocument }: Props) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<FileStatus>(file.status);
  const hasDocRevisionRole = rbac.hasDocRevisionRole();

  if (!document) return null;

  const changeStatus = async (nextStatus: FileStatus) => {
    if (!hasDocRevisionRole) {
      return notification.error({ message: `Role is required: ${ROLES.DOC_REVISION}` });
    }
    if (loading || status === nextStatus) {
      return;
    }
    setLoading(true);

    try {
      const updatedDocument = await creditHttp.changeFileStatus({
        creditId,
        file,
        status: nextStatus,
      });
      notification.success({ message: 'Status is updated' });
      setStatus(nextStatus);
      setDocument(updatedDocument);
    } catch (err: any) {
      notification.error({ message: axiosErrorToString(err) });
    } finally {
      setLoading(false);
    }
  };

  const isOk = status === FileStatus.APPROVED || status === FileStatus.SIGNED;

  return (
    <div className={cls.row}>
      <IconButton onClick={() => changeStatus(FileStatus.APPROVED)} title="Approve">
        {isOk ? <OkIcon /> : <OkDisabledIcon />}
      </IconButton>
      {!hasDocRevisionRole && (
        <LightTooltip title={`Role is required: ${ROLES.DOC_REVISION}`}>
          <LockOutlined className={cls.lockIcon} />
        </LightTooltip>
      )}
      <IconButton onClick={() => changeStatus(FileStatus.DENIED)} title="Deny">
        {status === FileStatus.DENIED ? <DeniedIcon /> : <DeniedDisabledIcon />}
      </IconButton>
    </div>
  );
};

const OkDisabledIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g opacity="0.35">
      <rect width="20" height="20" rx="10" fill="#275D3C" />
      <path d="M9.28577 11.9639L13.7501 7.49952"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round" />
      <path d="M9.28577 11.9639L7.50003 10.1781"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round" />
    </g>
  </svg>
);

const OkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="20" height="20" rx="10" fill="#275D3C" />
    <path d="M9.28577 11.9639L13.7501 7.49952"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round" />
    <path d="M9.28577 11.9639L7.50003 10.1781"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round" />
  </svg>
);

const DeniedDisabledIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g opacity="0.35">
      <path d="M10 0C4.47801 0 0 4.47801 0 10C0 15.522 4.47801 20 10 20C15.522 20 20 15.522 20 10C19.9911 4.47801 15.5175 0.00444247 10 0Z"
        fill="#FF4548" />
      <path d="M11.7132 9.80362L13.3947 11.5778C13.8684 12.0753 13.8684 12.8795 13.3947 13.3769C12.9211 13.8744 12.1553 13.8744 11.6816 13.3769L10 11.6027L8.31053 13.3686C7.84474 13.8661 7.07895 13.8661 6.60526 13.3686C6.13158 12.8712 6.13158 12.067 6.60526 11.5695L8.28684 9.80362L6.60526 8.02941C6.13158 7.54026 6.13158 6.73606 6.60526 6.23862C6.84211 5.9899 7.15 5.86554 7.45789 5.86554C7.76579 5.86554 8.07368 5.9899 8.31053 6.23862L10 8.00454L11.6895 6.23862C11.9263 5.9899 12.2342 5.86554 12.5421 5.86554C12.85 5.86554 13.1579 5.9899 13.3947 6.23862C13.8684 6.73606 13.8684 7.54026 13.3947 8.0377L11.7132 9.80362Z"
        fill="white" />
    </g>
  </svg>
);

const DeniedIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 0C4.47801 0 0 4.47801 0 10C0 15.522 4.47801 20 10 20C15.522 20 20 15.522 20 10C19.9911 4.47801 15.5175 0.00444247 10 0Z"
      fill="#FF4548" />
    <path d="M11.7132 9.80362L13.3947 11.5778C13.8684 12.0753 13.8684 12.8795 13.3947 13.3769C12.9211 13.8744 12.1553 13.8744 11.6816 13.3769L10 11.6027L8.31053 13.3686C7.84474 13.8661 7.07895 13.8661 6.60526 13.3686C6.13158 12.8712 6.13158 12.067 6.60526 11.5695L8.28684 9.80362L6.60526 8.02941C6.13158 7.54026 6.13158 6.73606 6.60526 6.23862C6.84211 5.9899 7.15 5.86554 7.45789 5.86554C7.76579 5.86554 8.07368 5.9899 8.31053 6.23862L10 8.00454L11.6895 6.23862C11.9263 5.9899 12.2342 5.86554 12.5421 5.86554C12.85 5.86554 13.1579 5.9899 13.3947 6.23862C13.8684 6.73606 13.8684 7.54026 13.3947 8.0377L11.7132 9.80362Z"
      fill="white" />
  </svg>
);
